import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, onSessionExpired } from './api';
import type { Session, Workspace } from '@lfb/core';
export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});
interface AuthState {
  session: Session | null;
  workspace: Workspace | undefined;
  selectWorkspace: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  demoAvailable: boolean;
  error: string;
}
const AuthContext = createContext<AuthState>({
  session: null,
  workspace: undefined,
  selectWorkspace: () => {},
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  demoAvailable: false,
  error: '',
});
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(() => localStorage.getItem('lfb-workspace') || '');
  const [demoAvailable, setDemoAvailable] = useState(false);
  const [error, setError] = useState('');
  const client = useQueryClient();
  const refresh = async () => {
    const next = await api<Session>('/session');
    setSession(next);
  };
  useEffect(() => {
    let alive = true;
    async function init() {
      try {
        const config = await api<{ demo: boolean }>('/config');
        if (alive) setDemoAvailable(config.demo);
        try {
          const next = await api<Session>('/session');
          if (alive) setSession(next);
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            if (config.demo && !location.pathname.startsWith('/invite')) {
              await api('/auth/demo', { method: 'POST' });
              const next = await api<Session>('/session');
              if (alive) setSession(next);
            }
          } else throw e;
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    }
    void init();
    return () => {
      alive = false;
    };
  }, []);
  // A 401 from any authenticated call ends this browser session (see api.ts onSessionExpired).
  useEffect(
    () =>
      onSessionExpired(() => {
        setSession(null);
        client.clear();
      }),
    [client],
  );
  const workspace =
    session?.workspaces.find((w) => w.id === workspaceId && w.status === 'active') ||
    session?.workspaces.find((w) => w.status === 'active');
  const selectWorkspace = (id: string) => {
    setWorkspaceId(id);
    localStorage.setItem('lfb-workspace', id);
    client.clear();
  };
  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    setSession(null);
    client.clear();
  };
  return (
    <AuthContext.Provider
      value={{
        session,
        workspace,
        selectWorkspace,
        loading,
        refresh,
        logout,
        demoAvailable,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
export const useAuth = () => useContext(AuthContext);
